/**
 * Next Lesson Widget
 * Shows next upcoming lesson with date/time and progress.
 * Moved from StudentDashboardBeta (Phase 3.2 extraction).
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { BookOpen } from 'lucide-react'
import { useTranslation } from '../../i18n/TranslationProvider'
import type { BffNextLesson } from '../../types/bff-v2'

export interface NextLessonWidgetProps {
  lesson: BffNextLesson | null
  className?: string
}

const ProgressBar: React.FC<{ pct: number }> = ({ pct }) => (
  <div className="h-[5px] bg-[var(--color-g200)] rounded-[5px] overflow-hidden mt-[10px]">
    <div
      className="h-full rounded-[5px] transition-[width] duration-600"
      style={{
        width: `${Math.min(100, pct)}%`,
        background: 'linear-gradient(90deg, var(--color-cyan), var(--color-neon))',
      }}
    />
  </div>
)

const NextLessonWidget: React.FC<NextLessonWidgetProps> = ({ lesson, className = '' }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  return (
    <div
      onClick={() => navigate('/dashboard/lessons')}
      className={`bg-white border border-[var(--color-border-light)] rounded-[var(--radius-r)]
                  p-[18px] shadow-[var(--shadow-sh0)]
                  transition-all duration-[.2s]
                  cursor-pointer hover:shadow-[var(--shadow-sh2)] hover:-translate-y-[2px]
                  active:translate-y-0 active:scale-[.99]
                  flex flex-col ${className}`}
    >
      <div className="flex items-center gap-[7px] text-[11px] font-semibold text-[var(--color-text-tertiary)]
                      uppercase tracking-[.5px] mb-[12px]">
        <BookOpen size={13} />
        {t('student.todays_lesson', "Today's Lesson")}
        <button
          onClick={(e) => { e.stopPropagation(); navigate('/dashboard/lessons') }}
          className="ml-auto text-[10px] text-[var(--color-text-tertiary)] font-medium
                     hover:text-[var(--color-teal)] transition-colors duration-[.15s]"
        >
          View →
        </button>
      </div>
      {lesson ? (
        <>
          <div className="text-[14px] font-bold text-[var(--color-text-primary)]">
            {lesson.title}
          </div>
          <div className="text-[12px] text-[var(--color-text-secondary)] mt-[2px]">
            {lesson.teacher_name} · {lesson.datetime}
          </div>
          <div className="text-[11px] text-[var(--color-text-tertiary)] mt-[1px]">
            {lesson.duration_min} min · {lesson.status}
          </div>
          <ProgressBar pct={lesson.progress_pct} />
        </>
      ) : (
        <div className="text-[13px] text-[var(--color-text-tertiary)]">
          {t('student.no_upcoming_lessons', 'No upcoming lessons')}
        </div>
      )}
    </div>
  )
}

export default NextLessonWidget
